import { luDecompose, luSolve } from './linalg';

/** 떠 있는 노드로 인한 특이행렬을 막기 위한 아주 작은 대지 컨덕턴스 */
export const GMIN = 1e-12;

/**
 * Modified Nodal Analysis 행렬.
 *
 *  [ G  B ] [ v ]   [ i ]
 *  [ C  D ] [ j ] = [ e ]
 *
 * 미지수 = 노드 전압 n개(접지 제외) + 전압원 가지전류 m개
 */
export class MnaMatrix {
  readonly n: number;
  readonly m: number;
  readonly size: number;
  readonly A: Float64Array;
  readonly b: Float64Array;
  readonly x: Float64Array;
  private readonly lu: Float64Array;
  private readonly pivot: Int32Array;

  constructor(nodeCount: number, vsCount: number) {
    this.n = nodeCount;
    this.m = vsCount;
    this.size = nodeCount + vsCount;
    const s = this.size;
    this.A = new Float64Array(s * s);
    this.b = new Float64Array(s);
    this.x = new Float64Array(s);
    this.lu = new Float64Array(s * s);
    this.pivot = new Int32Array(s);
  }

  clear(): void {
    this.A.fill(0);
    this.b.fill(0);
    // 모든 노드에 미세 컨덕턴스를 달아 완전히 떠 있는 회로도 풀리게 한다
    for (let i = 0; i < this.n; i++) this.A[i * this.size + i] += GMIN;
  }

  addA(i: number, j: number, value: number): void {
    if (i < 0 || j < 0) return; // 접지 노드는 방정식에서 제외
    this.A[i * this.size + j] += value;
  }

  addB(i: number, value: number): void {
    if (i < 0) return;
    this.b[i] += value;
  }

  /** 노드 a-b 사이 컨덕턴스 g */
  conductance(a: number, b: number, g: number): void {
    this.addA(a, a, g);
    this.addA(b, b, g);
    this.addA(a, b, -g);
    this.addA(b, a, -g);
  }

  /** 노드 a-b 사이 저항 r */
  resistor(a: number, b: number, r: number): void {
    this.conductance(a, b, 1 / Math.max(r, 1e-9));
  }

  /** a에서 나와 소자를 거쳐 b로 들어가는 정전류 i */
  currentSource(a: number, b: number, i: number): void {
    this.addB(a, -i);
    this.addB(b, i);
  }

  /** 이상 전압원: v = V(b) - V(a)... 여기서는 V(a) - V(b) = v 로 정의 */
  voltageSource(a: number, b: number, vsIndex: number, v: number): void {
    const r = this.n + vsIndex;
    this.addA(a, r, 1);
    this.addA(b, r, -1);
    this.addA(r, a, 1);
    this.addA(r, b, -1);
    this.b[r] += v;
  }

  /**
   * 선형화된 다단자 소자 스탬프.
   * 단자 k 로 소자에 들어가는 전류  I_k = C[k] + Σ_j G[k][j] · V(nodes[j])
   */
  linearized(nodes: number[], G: number[][], C: number[]): void {
    for (let k = 0; k < nodes.length; k++) {
      const row = nodes[k];
      if (row < 0) continue;
      for (let j = 0; j < nodes.length; j++) {
        const g = G[k][j];
        if (g !== 0) this.addA(row, nodes[j], g);
      }
      this.b[row] -= C[k];
    }
  }

  solve(): boolean {
    this.lu.set(this.A);
    if (!luDecompose(this.lu, this.size, this.pivot)) return false;
    luSolve(this.lu, this.size, this.pivot, this.b, this.x);
    for (let i = 0; i < this.size; i++) {
      if (!Number.isFinite(this.x[i])) return false;
    }
    return true;
  }

  /** 노드 전압 (접지는 0) */
  nodeVoltage(node: number): number {
    return node < 0 ? 0 : this.x[node];
  }

  /** 전압원 가지 전류 (a -> b 방향) */
  branchCurrent(vsIndex: number): number {
    return this.x[this.n + vsIndex];
  }
}
